<template>
  <section
    class="worldMapTop"
  >
    <div class="title__wrap">
      <SectionTitle
        :title="`${areaLabelMap[props.areaId]}の天気`"
        is-h1
      />
      <ShareButton
        category="AREA"
        :title="pageTitle"
      />
    </div>
    <div
      class="map"
      :data-display-area="props.areaId"
    >
      <img
        :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/map/area/${props.areaId}.webp`"
        alt=""
        class="map__img"
      >
      <div class="links">
        <!-- CSS のdynamic import時のグニャを軽減するため -->
        <ClientOnly>
          <template v-if="displayMapPoint">
            <div
              v-for="item in displayCountry[props.areaId]"
              :key="item.text"
              class="city"
              :data-country="item.key"
            >
              <NuxtLink
                :to="getLinkUrl(item)"
                :external="isExternalLink"
                :data-area="item.areaId ?? props.areaId"
                class="map__link"
              >
                <figure class="flag">
                  <img
                    :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${item.cc}.webp`"
                    width="20"
                    height="15"
                    alt=""
                  >
                </figure>
                <span class="map__text">
                  {{ item.text }}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="7"
                  height="11"
                  viewBox="0 0 7 11"
                  fill="none"
                >
                  <path
                    d="M1 1.5L6 5.5L1 9.5"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                </svg>
              </NuxtLink>
            </div>
          </template>
        </ClientOnly>
      </div>
    </div>
    <TableList
      :area-id="areaId"
      :list="otherCountry"
      :target-name="`${areaLabelMap[areaId]}の国々`"
      target="area"
    />
  </section>
</template>

<script setup>
import displayCountry from '~/data/table/area_display_country.json'
import countryIdList from '~/data/table/country_id_list.json'

const props = defineProps({
  areaId: {
    type: String,
    required: true
  },
  pageTitle: {
    type: String,
    required: true
  },
  now: {
    type: Number,
    required: true
  }
})

const displayMapPoint = ref(false)

const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
const isExternalLink = ref(isWebViewFlag)

const otherCountry = []
const displayCountryKeys = displayCountry[props.areaId].map((item) => item.key)
Object.entries(countryIdList).forEach(([key, value]) => {
  const [_, areaId_, countryKey, __] = key.split('/')
  if (areaId_ === props.areaId && !displayCountryKeys.includes(countryKey)) {
    otherCountry.push({
      key: countryKey,
      text: value.country_ja,
      cc: value.cc
    })
  }
})

const getLinkUrl = (item) => {
  const linkTargetAreaId = item.areaId ?? props.areaId
  return isExternalLink.value
    ? `${runtimeConfig.app.baseURL}${linkTargetAreaId}/${item.key}/`
    : `/${linkTargetAreaId}/${item.key}/`
}

onMounted(async () => {
  await import(`~/assets/styles/area/${props.areaId}.scss`)
  displayMapPoint.value = true
})
</script>

<style lang="scss" scoped>
.worldMapTop {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .title__wrap {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 5px;
    flex-wrap: wrap;
  }

  .map {
    position: relative;
    background: linear-gradient(180deg, #FEFEFE 0%, #E8F8FF 100%);

    .map__img {
      width: 100%;
      aspect-ratio: 1512 / 922;
    }

    .map__link {
      position: relative;
      top: auto;
      left: auto;
    }

    .city {
      position: absolute;
      .flag {
        aspect-ratio: 1;
        border: 2px solid #ccc;
        border-radius: 50%;
        overflow: hidden;

        img {
          display: block;
          filter: saturate(60%) brightness(130%);
          width: 20px;
          height: 100%;
          object-fit: cover;
        }
      }
    }

    &__link {
      position: absolute;
      display: flex;
      align-items: center;
      gap: min(1.33vw, 7px);
      padding: min(1.07vw, 8px) min(2.13vw, 11px) min(1.07vw, 8px) min(3vw, 15px);
      border-radius: 100vmax;
      border: 2px solid #FFF;
      box-shadow: 2px 2px 4px 0 rgb(0 0 0 / .25);

      &[data-area="5"] {
        background-color: #527E23;
      }

      &[data-area="9"] {
        background-color: #C77928;
      }

      &[data-area="4"] {
        background-color: #4577CB;
      }

      &[data-area="1"] {
        background-color: #4577CB;
      }

      &[data-area="3"] {
        background-color: #4577CB;
      }

      &[data-area="2"] {
        background-color: #4577CB;
      }

      &[data-area="6"] {
        background-color: #BA507E;
      }

      &[data-area="7"] {
        background-color: #986EB1;
      }

      &[data-area="8"] {
        background-color: #A89B2B;
      }

      svg {
        fill: transparent;
      }

      path {
        stroke: #fff;
      }
    }

    &__text {
      color: #FFF;
      font-family: "Hiragino Sans";
      font-size: min(3.2vw, 22px);
      font-weight: 600;
      line-height: 1em;
      letter-spacing: .18px;
      white-space: nowrap;
      user-select: none;
    }

    .balloon {
      position: absolute;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: min(.36vw, 3px);
      width: min(9vw, 75px);
      padding: min(.6vw, 5px) min(.24vw, 2px);
      border-radius: min(1.2vw, 10px);
      border: none;
      background-color: #fff;
      box-shadow: 0 4px 4px 0 rgb(0 0 0 / .25);

      figure {
        width: min(8.2vw, 68px);
        aspect-ratio: 68 / 50;

        img {
          width: 100%;
          height: 100%;
        }
      }

      &::before {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: min(2vw, 17px);
        height: min(1.8vw, 15px);
        background-color: #fff;
        content: '';
      }

      &[data-positionX="left"] {
        left: 8%;
      }

      &[data-positionX="right"] {
        left: 42%;
      }

      &[data-positionY="top"] {
        bottom: 120%;

        &::before {
          bottom: max(-1.7vw, -14px);
          clip-path: polygon(0 0, 100% 0, 50% 100%);
        }
      }

      &[data-positionY="bottom"] {
        top: 120%;

        &::before {
          top: max(-1.7vw, -14px);
          clip-path: polygon(50% 0, 100% 100%, 0 100%);
        }
      }

      .temp {
        display: flex;
        align-items: center;
        gap: min(.72vw, 6px);
        color: #aaa;
        font-size: min(2.4vw, 20px);
        font-weight: 600;
        line-height: 1em;

        .max {
          color: #F64D00;
        }

        .min {
          color: #0075F3;
        }
      }
    }
  }

  @media (any-hover: hover) {
    .map {
      &__link {
        transition: background-color .2s ease-in-out, border-color .2s ease-in-out;

        path {
          transition: stroke .2s ease-in-out;
        }

        &:hover {
          background-color: #fff;

          &[data-area="5"] {
            border-color: #527E23;

            path {
              stroke: #527E23;
            }

            .map__text {
              color: #527E23;
            }
          }

          &[data-area="9"] {
            border-color: #C77928;

            path {
              stroke: #C77928;
            }

            .map__text {
              color: #C77928;
            }
          }

          &[data-area="4"],
          &[data-area="1"],
          &[data-area="3"],
          &[data-area="2"] {
            border-color: #4577CB;

            path {
              stroke: #4577CB;
            }

            .map__text {
              color: #4577CB;
            }
          }

          &[data-area="6"] {
            border-color: #BA507E;

            path {
              stroke: #BA507E;
            }

            .map__text {
              color: #BA507E;
            }
          }

          &[data-area="7"] {
            border-color: #986EB1;

            path {
              stroke: #986EB1;
            }

            .map__text {
              color: #986EB1;
            }
          }

          &[data-area="8"] {
            border-color: #A89B2B;

            path {
              stroke: #A89B2B;
            }

            .map__text {
              color: #A89B2B;
            }
          }
        }
      }

      &__text {
        transition: color .2s ease-in-out;
      }
    }
  }
}

.main {
  .worldMapTop {
    @media screen and (min-width: 830px) {
      .map {
        &__link {
          gap: min(.58vw, 7px);
          padding: min(.58vw, 7px) min(.92vw, 11px) min(.5vw, 6px) min(1.25vw, 15px);
        }

        &__text {
          font-size: min(1.42vw, 17px);
        }

        .balloon {
          gap: min(.28vw, 3px);
          width: min(7vw, 75px);
          padding: min(.47vw, 5px) min(.19vw, 2px);
          border-radius: min(.93vw, 10px);

          figure {
            width: min(6.4vw, 68px);
          }

          &::before {
            width: min(1.6vw, 17px);
            height: min(1.4vw, 15px);
          }

          &[data-positionY="top"] {
            &::before {
              bottom: max(-1.3vw, -14px);
            }
          }

          &[data-positionY="bottom"] {
            &::before {
              top: max(-1.3vw, -14px);
            }
          }

          .temp {
            gap: min(.56vw, 6px);
            font-size: min(1.87vw, 20px);
          }
        }
      }
    }
  }
}

.list {
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid #CCC;
  border-left: 1px solid #CCC;
}
.link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  width: 50%;
  padding: 6px;
  background-color: #FFF;
  border-bottom: 1px solid #CCC;
  border-right: 1px solid #CCC;
}
.text {
  display: flex;
  align-items: center;
  gap: 3px;
  .inner {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }
  .flag{
      border: #ccc solid 1px;
      border-radius: 50%;
      overflow: hidden;
      aspect-ratio: 1;
      img {
        display: block;
        filter: saturate(60%) brightness(130%);
        object-fit: cover;
        height: 100%;
      }
  }
  .area {
    color: #3569C0;
    font-size: 13px;
    font-weight: 600;
    line-height: 14px;
    text-decoration: underline;
  }
  .time {
    color: #606060;
    font-size: 10px;
    line-height: 1em;
  }
}
.info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3px;
  min-width: 63px;
  .icon {
    width: 30px;
    aspect-ratio: 34 / 25;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .temp {
    display: flex;
    flex-direction: column;
    gap: 3px;
    p {
      text-align: right;
      font-size: 13px;
      font-weight: 600;
      line-height: 1em;
      &.max {
        color: #F64D00;
      }
      &.min {
        color: #0075F3;
      }
    }
  }
}
@media (any-hover: hover) {
  .link {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
  }
}
@media screen and (min-width: 830px) {
  .list {
    gap: 7px;
    border-top: none;
    border-left: none;
  }
  .link {
    display: flex;
    align-items: center;
    gap: 8px;
    width: calc((100% - 14px) / 3);
    padding: 3px 8px;
    background-color: #FFF;
    border: 1px solid #CCC;
    border-radius: 3px;
  }
  .text {
    .area {
      text-align: left;
    }
    .time {
      text-align: left
    }
  }
}
</style>
